import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

/**
 * Settings → Tor bridges tab.
 *
 * Operator-managed pool of bridge lines that the microservice ships to
 * every terminal in `welcome.meshTorBridges`. Replaces the legacy
 * hardcoded bridge list that previously had to be re-released
 * via APK whenever DPI burned a batch.
 *
 * Layout:
 *  • Header card — "Add bridges" — contains two ways to populate the
 *    pool: (a) Sources sub-section (URL fetchers; round 62) and
 *    (b) "Bridge lines" textarea for direct paste.
 *  • Per-kind sections (obfs4 / snowflake / meek_lite / webtunnel /
 *    vanilla). Each section header has a "Batch check" button that
 *    probes every row of that kind. Each row has:
 *      ✓ enabled checkbox (auto-set by check; manual toggle still works)
 *      line preview (click to expand)
 *      ✎ Edit (line + comment in a modal)
 *      ↻ Check (probe alive/dead; auto-toggles enabled flag)
 *      🗑 Delete
 *
 * Mutations are immediate (no draft / batch save): single source of
 * truth lives on the server so any admin watching elsewhere sees the
 * change without a refresh. Each successful mutation also bumps the
 * policy version → microservice republishes welcome to every
 * connected terminal.
 */
const KIND_LABELS = Object.freeze({
  obfs4:      'obfs4',
  snowflake:  'Snowflake',
  meek_lite:  'meek_lite',
  webtunnel:  'WebTunnel',
  vanilla:    'Vanilla',
});

/**
 * 2026-06-10 — WebTunnel fully supported end-to-end. The TMS mobile
 * APK ships the patched onionwrapper-core fork (0.1.4-tms1) that
 * registers the webtunnel ClientTransportPlugin in Tor's torrc;
 * bundled lyrebird-android 0.6.2 has carried the webtunnel-client
 * PT since upstream v0.2.0 (March 2024). Server's
 * listEnabledBridgeLines() ships {@code kind='webtunnel'} rows to
 * the terminal; the operator picks WebTunnel from the bridge-type
 * selector like any other PT.
 *
 * Bridge-line shape per Tor PT spec:
 *   webtunnel &lt;ip&gt;:&lt;port&gt; &lt;fingerprint&gt; url=https://... ver=0.0.1
 */
const KIND_ORDER = ['obfs4', 'snowflake', 'meek_lite', 'webtunnel', 'vanilla'];

/**
 * 2026-05-30 — network-reach badges per bridge kind. Surfaces the
 * actual IPv4/IPv6 coverage so operators don't mistake meek_lite for
 * "IPv6-only" (the previous misleading label — fixed after the field
 * investigation showed meek_lite works fine on plain IPv4 because its
 * client just opens an HTTPS connection to a major CDN endpoint).
 */
const KIND_NETWORK = Object.freeze({
  obfs4:      'IPv4 (dominant), IPv6 (if bridge advertises AAAA)',
  snowflake:  'IPv4 and IPv6',
  meek_lite:  'IPv4 and IPv6 — wherever a CDN is reachable',
  webtunnel:  'IPv4 and IPv6',
  vanilla:    'IPv4 and IPv6',
});

export function BridgesTab({ currentUser } = {}) {
  const toast = useToast();
  const [bridges, setBridges] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Per-bridge check state — Map<id, 'checking' | 'alive' | 'dead'>
  const [probing, setProbing] = useState(() => new Map());
  // Edit dialog — null or { id, line, comment }
  const [editing, setEditing] = useState(null);
  // Form state for the "add" pane.
  const [draftLines, setDraftLines] = useState('');
  const [draftComment, setDraftComment] = useState('');

  async function reload() {
    try {
      const { bridges } = await api.torBridges.list();
      setBridges(bridges);
      setLoadError(null);
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.payload?.error || err.message : 'Failed to load bridges.';
      setLoadError(msg);
    }
  }
  useEffect(() => { reload(); }, []);

  const grouped = useMemo(() => {
    if (!bridges) return null;
    const out = {};
    for (const b of bridges) {
      (out[b.kind] = out[b.kind] || []).push(b);
    }
    return out;
  }, [bridges]);

  /** Bulk-add: split textarea into non-empty lines, POST each. Failures
   *  on one line don't stop the rest — collect outcomes for a summary
   *  toast at the end. */
  async function handleAdd() {
    const lines = draftLines.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast.error('Paste at least one bridge line.');
      return;
    }
    setBusy(true);
    let added = 0;
    const failures = [];
    for (const line of lines) {
      try {
        await api.torBridges.create({
          line,
          comment: draftComment.trim() || null,
        });
        added += 1;
      } catch (err) {
        failures.push({
          line: line.length > 50 ? line.slice(0, 50) + '…' : line,
          msg:  err instanceof ApiError
            ? err.payload?.error || err.message : 'unknown',
        });
      }
    }
    await reload();
    setBusy(false);
    if (added > 0) {
      setDraftLines('');
      setDraftComment('');
      toast.success(`${added} bridge(s) added.`);
    }
    if (failures.length > 0) {
      toast.error(
        `${failures.length} line(s) rejected: ${failures[0].msg}` +
        (failures.length > 1 ? ' (+ more)' : ''),
      );
    }
  }

  async function handleToggle(b) {
    setBusy(true);
    try {
      await api.torBridges.update(b.id, { enabled: !b.enabled });
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Toggle failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(b) {
    if (!window.confirm(`Delete this bridge?\n\n${b.line}`)) return;
    setBusy(true);
    try {
      await api.torBridges.remove(b.id);
      await reload();
      toast.success('Bridge deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  /** Round 61 — per-row reachability check. Marks probing locally for
   *  spinner UX, posts to backend, server flips `enabled` server-side
   *  so reload() reflects the new state. */
  async function handleCheck(b) {
    setProbing((m) => new Map(m).set(b.id, 'checking'));
    try {
      const { result } = await api.torBridges.check(b.id);
      setProbing((m) => new Map(m).set(b.id, result.alive ? 'alive' : 'dead'));
      if (result.alive) toast.success(`Bridge alive: ${result.message}`);
      else toast.error(`Bridge dead: ${result.message}`);
      await reload();
      // Clear the per-bridge probe badge after a short delay so the
      // alive/dead glow doesn't outlive the operator's attention span.
      setTimeout(() => {
        setProbing((m) => {
          const next = new Map(m);
          next.delete(b.id);
          return next;
        });
      }, 5000);
    } catch (err) {
      setProbing((m) => {
        const next = new Map(m);
        next.delete(b.id);
        return next;
      });
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Check failed.');
    }
  }

  /** Round 62b — flip `enabled` for every row of a kind in one server call.
   *  Single policy version bump for the whole batch. */
  async function handleBulkEnable(kind, enabled) {
    setBusy(true);
    try {
      const result = await api.torBridges.bulkEnable(kind, enabled);
      await reload();
      if (result.changed > 0) {
        toast.success(
          `${KIND_LABELS[kind]}: ${result.changed} row(s) ${enabled ? 'enabled' : 'disabled'}.`,
        );
      } else {
        toast.success(`${KIND_LABELS[kind]}: already ${enabled ? 'enabled' : 'disabled'}.`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Bulk update failed.');
    } finally {
      setBusy(false);
    }
  }

  /** Round 64e — wipe every row of a kind. Destructive — confirm
   *  before calling. Empty pool of that kind after the call. Single
   *  policy version bump. */
  async function handleBulkDelete(kind) {
    const count = (grouped[kind] || []).length;
    if (count === 0) {
      toast.error(`${KIND_LABELS[kind]}: nothing to clear.`);
      return;
    }
    if (!window.confirm(
      `Delete every ${KIND_LABELS[kind]} bridge (${count} row${count === 1 ? '' : 's'})?\n\n` +
      `This wipes the rows from the database. You'll need to re-add them.`,
    )) return;
    setBusy(true);
    try {
      const result = await api.torBridges.bulkDelete(kind);
      await reload();
      toast.success(`${KIND_LABELS[kind]}: ${result.deleted} row(s) deleted.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Bulk delete failed.');
    } finally {
      setBusy(false);
    }
  }

  /** 2026-06-10 — wipe only the disabled rows of a kind (the ones
   *  the batch checker marked as dead). Loud confirm with the count
   *  of rows that will be deleted. Single policy bump if any change. */
  async function handleBulkDeleteInactive(kind) {
    const inactive = (grouped[kind] || []).filter((b) => !b.enabled);
    if (inactive.length === 0) {
      toast.error(`${KIND_LABELS[kind]}: no inactive rows to delete.`);
      return;
    }
    if (!window.confirm(
      `Delete every INACTIVE ${KIND_LABELS[kind]} bridge (${inactive.length} row${inactive.length === 1 ? '' : 's'})?\n\n` +
      `Active (enabled) rows stay in the pool. Inactive ones are wiped from the database.`,
    )) return;
    setBusy(true);
    try {
      const result = await api.torBridges.bulkDeleteInactive(kind);
      await reload();
      toast.success(`${KIND_LABELS[kind]}: ${result.deleted} inactive row(s) deleted.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Bulk delete inactive failed.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * 2026-07-25 — Export ACTIVE bridges of one kind to a JSON file.
   * Downloads in-browser via Blob; no server round-trip.
   *
   * File shape (schema `tms-tor-bridges/v1`):
   *   {
   *     "schema": "tms-tor-bridges/v1",
   *     "exportedAt": ISO-8601 UTC,
   *     "exportedBy": operator username (or "unknown"),
   *     "kind": "obfs4"|"snowflake"|"meek_lite"|"webtunnel"|"vanilla",
   *     "count": <bridges.length>,
   *     "bridges": [ { line, comment, createdAt } ]
   *   }
   *
   * Round-trip contract: an importer can rebuild rows from `line`
   * (required), preserving `comment` + `createdAt` when present.
   */
  function handleExport(kind) {
    const rows = (grouped?.[kind] || []).filter((b) => b.enabled);
    if (rows.length === 0) {
      toast.error(`${KIND_LABELS[kind]}: no active bridges to export.`);
      return;
    }
    const payload = {
      schema:     'tms-tor-bridges/v1',
      exportedAt: new Date().toISOString(),
      exportedBy: currentUser?.username || 'unknown',
      kind,
      count:      rows.length,
      bridges:    rows.map((b) => ({
        line:      b.line,
        comment:   b.comment ?? null,
        createdAt: b.created_at || null,
      })),
    };
    const json = JSON.stringify(payload, null, 2);
    const yyyyMmDd = new Date().toISOString().slice(0, 10);
    const filename = `tor-bridges-${kind}-${yyyyMmDd}.json`;
    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${KIND_LABELS[kind]}: exported ${rows.length} active bridge(s) → ${filename}`);
    } catch (err) {
      toast.error(`Export failed: ${err?.message || 'unknown error'}`);
    }
  }

  /** Round 61 — batch reachability check for every row of a kind. */
  async function handleBatchCheck(kind) {
    const ids = (grouped[kind] || []).map((b) => b.id);
    if (ids.length === 0) {
      toast.error(`No ${KIND_LABELS[kind]} bridges to check.`);
      return;
    }
    setBusy(true);
    setProbing((m) => {
      const next = new Map(m);
      ids.forEach((id) => next.set(id, 'checking'));
      return next;
    });
    try {
      const summary = await api.torBridges.checkBatch(kind);
      setProbing((m) => {
        const next = new Map(m);
        for (const r of summary.results || []) {
          if (r?.id != null) {
            next.set(r.id, r.alive ? 'alive' : 'dead');
          }
        }
        return next;
      });
      await reload();
      toast.success(
        `${KIND_LABELS[kind]}: ${summary.alive} alive, ${summary.dead} dead of ${summary.count}.`,
      );
      setTimeout(() => {
        setProbing((m) => {
          const next = new Map(m);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }, 7000);
    } catch (err) {
      setProbing((m) => {
        const next = new Map(m);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Batch check failed.');
    } finally {
      setBusy(false);
    }
  }

  function openEdit(b) {
    setEditing({ id: b.id, line: b.line, comment: b.comment || '' });
  }
  async function handleEditSave({ id, line, comment }) {
    setBusy(true);
    try {
      await api.torBridges.update(id, {
        line: line.trim(),
        comment: comment.trim() || null,
      });
      await reload();
      setEditing(null);
      toast.success('Bridge updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div role="alert" className="card border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
        {loadError}
      </div>
    );
  }
  if (!bridges) {
    return (
      <div className="card px-6 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
        Loading bridges…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <i className="fa-solid fa-bridge" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">Add bridges</h2>
              <p className="text-sm text-mist">
                Two ways to populate the pool: register a Source URL that
                publishes bridge lines, or paste lines directly into the
                "Bridge lines" textarea below. Recognised prefixes: obfs4 /
                snowflake / meek_lite / webtunnel / vanilla (bare IP:port).
                Each addition bumps the policy version, so connected terminals
                pick up the new list on the next welcome refresh (no APK
                redeploy needed).
              </p>
            </div>
          </div>
        </header>
        <div className="space-y-6 px-6 py-6">

          {/* Round 62 — Sources sub-section. URL fetchers that publish
              bridge lines (one per non-blank, non-comment line). Each
              fetch parses the response, dedup-guards each line, and
              imports new entries into the pool. */}
          <SourcesSection onBridgesAdded={reload} />

          <div className="border-t border-line" />

          {/* Manual paste — pre-existing form, kept verbatim. */}
          <div className="space-y-4">
            <div>
              <h3 className="mb-1 text-sm font-semibold text-ink">Bridge lines (manual paste)</h3>
              <p className="mb-2 text-xs text-mist">
                Paste full bridge lines from bridges.torproject.org or a
                private supplier. One bridge per line.
              </p>
              <label htmlFor="bridges_lines" className="field-label">Bridge lines</label>
              <textarea
                id="bridges_lines"
                rows={4}
                className="field font-mono text-xs"
                placeholder={'obfs4 1.2.3.4:443 1234567890ABCDEF cert=AbCd... iat-mode=0\nsnowflake 192.0.2.3:80 2B280B23E1107BB62...'}
                value={draftLines}
                onChange={(e) => setDraftLines(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="bridges_comment" className="field-label">Comment (optional)</label>
              <input
                id="bridges_comment"
                type="text"
                className="field"
                placeholder="e.g. batch from torproject 2026-05-13"
                maxLength={512}
                value={draftComment}
                onChange={(e) => setDraftComment(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex items-center justify-end">
              <button
                type="button"
                className="btn-primary"
                onClick={handleAdd}
                disabled={busy || draftLines.trim().length === 0}
              >
                {busy ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {bridges.length === 0 ? (
        <div className="card px-6 py-12 text-center text-mist">
          No bridges configured yet. Terminals are using their bundled defaults.
        </div>
      ) : (
        KIND_ORDER.filter((k) => grouped[k]?.length > 0).map((k) => (
          <div className="card" key={k}>
            <header className="flex items-center justify-between border-b border-line px-6 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-ink">
                  {KIND_LABELS[k]} <span className="text-mist">— {grouped[k].length}</span>
                </h3>
                {/* Network-reach badge — same look on every kind for
                    visual consistency. Slate = informational. */}
                <span
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                  title="Networks this bridge type can reach. Affects which terminals can use it depending on their ISP / SIM IP allocation."
                >
                  {KIND_NETWORK[k]}
                </span>
                {/* Forensic-only types still flagged in red so the
                    operator knows lines added here aren't reaching
                    any mobile terminal even though they're stored. */}
                {k === 'vanilla' && (
                  <span
                    className="rounded-full bg-rose-soft px-2 py-0.5 text-[11px] font-medium text-rose-700"
                    title="TMS AMC terminals only request NON_DEFAULT_OBFS4 + SNOWFLAKE + MEEK bridge types; vanilla rows are kept here for forensics but never reach the terminal's torrc."
                  >
                    not shipped to terminals
                  </span>
                )}
                {/* 2026-06-10 — WebTunnel "not shipped to terminals" badge
                    removed. TMS mobile APK now bundles the patched
                    onionwrapper-core fork (0.1.4-tms1) which registers
                    the webtunnel ClientTransportPlugin; bundled
                    lyrebird-android 0.6.2 has carried the
                    webtunnel-client PT since v0.2.0 (March 2024).
                    Server-side listEnabledBridgeLines() no longer
                    filters out kind='webtunnel'. */}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-mist">
                  {grouped[k].filter((b) => b.enabled).length} active
                </span>
                <button
                  type="button"
                  className="btn-ghost text-emerald-700"
                  onClick={() => handleBulkEnable(k, true)}
                  disabled={busy}
                  title={`Enable every ${KIND_LABELS[k]} bridge — one policy bump`}
                >
                  <i className="fa-solid fa-square-check mr-1.5" aria-hidden="true" />
                  Enable all
                </button>
                <button
                  type="button"
                  className="btn-ghost text-rose-700"
                  onClick={() => handleBulkEnable(k, false)}
                  disabled={busy}
                  title={`Disable every ${KIND_LABELS[k]} bridge — one policy bump`}
                >
                  <i className="fa-regular fa-square mr-1.5" aria-hidden="true" />
                  Disable all
                </button>
                <button
                  type="button"
                  className="btn-ghost text-accent"
                  onClick={() => handleBatchCheck(k)}
                  disabled={busy}
                  title={`Check every ${KIND_LABELS[k]} bridge in parallel`}
                >
                  <i className="fa-solid fa-magnifying-glass-arrow-right mr-1.5" aria-hidden="true" />
                  Batch check
                </button>
                {/* 2026-06-10 — narrower destructive sweep. Removes
                    only the rows the batch checker has marked as
                    inactive (enabled = 0). Active rows kept. */}
                <button
                  type="button"
                  className="btn-ghost text-rose-700"
                  onClick={() => handleBulkDeleteInactive(k)}
                  disabled={busy}
                  title={`Delete only the inactive ${KIND_LABELS[k]} bridges (active rows kept)`}
                >
                  <i className="fa-solid fa-filter-circle-xmark mr-1.5" aria-hidden="true" />
                  Delete inactive
                </button>
                {/* Round 64e — destructive nuke for this kind. Loud
                    confirm in the handler; visual cue via rose-700. */}
                <button
                  type="button"
                  className="btn-ghost text-rose-700"
                  onClick={() => handleBulkDelete(k)}
                  disabled={busy}
                  title={`Delete every ${KIND_LABELS[k]} bridge`}
                >
                  <i className="fa-solid fa-trash mr-1.5" aria-hidden="true" />
                  Clear list
                </button>
                {/* 2026-07-25 — download ACTIVE bridges of this kind
                    as a portable JSON file (schema tms-tor-bridges/v1).
                    Import path is the inverse — see file schema in
                    handleExport() javadoc above. Disabled when no
                    active rows to avoid downloading empty payloads. */}
                <button
                  type="button"
                  className="btn-ghost text-accent"
                  onClick={() => handleExport(k)}
                  disabled={busy || grouped[k].filter((b) => b.enabled).length === 0}
                  title={`Export active ${KIND_LABELS[k]} bridges to a JSON file`}
                >
                  <i className="fa-solid fa-download mr-1.5" aria-hidden="true" />
                  Export
                </button>
              </div>
            </header>
            <div className="divide-y divide-line">
              {grouped[k].map((b) => (
                <BridgeRow
                  key={b.id}
                  bridge={b}
                  busy={busy}
                  probeState={probing.get(b.id) || null}
                  onToggle={() => handleToggle(b)}
                  onEdit={() => openEdit(b)}
                  onCheck={() => handleCheck(b)}
                  onDelete={() => handleDelete(b)}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {editing && (
        <EditBridgeModal
          initial={editing}
          busy={busy}
          onSave={handleEditSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Single row — toggle + truncated line + comment / metadata + actions. */
function BridgeRow({ bridge, busy, probeState, onToggle, onEdit, onCheck, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const preview = bridge.line.length > 96 && !expanded
    ? bridge.line.slice(0, 96) + '…'
    : bridge.line;
  const probeBadge =
    probeState === 'checking' ? { icon: 'fa-spinner fa-spin', cls: 'text-mist', label: 'Probing…' } :
    probeState === 'alive'    ? { icon: 'fa-circle-check',     cls: 'text-emerald-600', label: 'Alive' } :
    probeState === 'dead'     ? { icon: 'fa-circle-xmark',     cls: 'text-rose-600',    label: 'Dead' } :
    null;
  return (
    <div className={
      'flex items-start gap-3 px-6 py-4 ' +
      (bridge.enabled ? '' : 'opacity-60')
    }>
      <label className="mt-1 inline-flex shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          checked={bridge.enabled}
          onChange={onToggle}
          disabled={busy}
          className="h-4 w-4"
          title={bridge.enabled ? 'Click to disable (shipping stops on next push)' : 'Click to enable'}
        />
      </label>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="break-all text-left font-mono text-xs text-ink"
          title={expanded ? 'Click to collapse' : 'Click to show full line'}
        >
          {preview}
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-mist">
          {probeBadge && (
            <span className={probeBadge.cls}>
              <i className={`fa-solid ${probeBadge.icon} mr-1`} aria-hidden="true" />
              {probeBadge.label}
            </span>
          )}
          {bridge.comment && (
            <span>
              <i className="fa-solid fa-quote-left mr-1" aria-hidden="true" />
              {bridge.comment}
            </span>
          )}
          <span>
            <i className="fa-solid fa-calendar-plus mr-1" aria-hidden="true" />
            {new Date(bridge.createdAt).toLocaleString()}
          </span>
          {bridge.createdBy && (
            <span>
              <i className="fa-solid fa-user mr-1" aria-hidden="true" />
              {bridge.createdBy}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          className="btn-ghost text-ink"
          onClick={onEdit}
          disabled={busy}
          title="Edit"
        >
          <i className="fa-solid fa-pen" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn-ghost text-accent"
          onClick={onCheck}
          disabled={busy || probeState === 'checking'}
          title="Check reachability — auto-toggles enabled flag"
        >
          <i className={probeState === 'checking'
            ? 'fa-solid fa-spinner fa-spin'
            : 'fa-solid fa-bolt'} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn-ghost text-rose-600 hover:bg-rose-soft"
          onClick={onDelete}
          disabled={busy}
          title="Delete"
        >
          <i className="fa-solid fa-trash" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * Round 62 — Sources sub-section embedded inside the "Add bridges"
 * widget. Lists every registered source URL with per-row actions
 * (Fetch / Edit / Delete / Toggle), plus an "Add source" inline form
 * above the list, and a "Fetch all" button.
 *
 * Each Fetch hit the backend which: (a) GETs the URL, (b) parses
 * non-blank/non-comment lines, (c) imports new bridges (existing
 * lines skipped as duplicates), (d) records per-fetch counters back
 * on the source row.
 *
 * The onBridgesAdded callback is fired by the parent to re-load the
 * main bridge pool whenever a Fetch imports rows.
 */
function SourcesSection({ onBridgesAdded }) {
  const toast = useToast();
  const [sources, setSources] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  // Per-source spinner state.
  const [fetching, setFetching] = useState(() => new Set());
  // Add-source form. Round 63 follow-up — {@code Comment} was renamed
  // to a required {@code Name} that surfaces wherever sources appear
  // (rows here + Schedule modal Source select).
  const [draftUrl, setDraftUrl]   = useState('');
  const [draftKind, setDraftKind] = useState('');
  const [draftName, setDraftName] = useState('');
  // Edit modal.
  const [editing, setEditing] = useState(null);

  async function reload() {
    try {
      const { sources } = await api.torBridgeSources.list();
      setSources(sources);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to load sources.');
    }
  }
  useEffect(() => { reload(); }, []);

  async function handleAdd() {
    if (!draftName.trim()) {
      toast.error('Name is required.');
      return;
    }
    if (!draftUrl.trim()) {
      toast.error('URL is required.');
      return;
    }
    setAdding(true);
    try {
      await api.torBridgeSources.create({
        name: draftName.trim(),
        url:  draftUrl.trim(),
        kind: draftKind || null,
      });
      setDraftName('');
      setDraftUrl('');
      setDraftKind('');
      await reload();
      toast.success('Source added.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Add failed.');
    } finally {
      setAdding(false);
    }
  }

  async function handleFetch(s) {
    setFetching((set) => new Set(set).add(s.id));
    try {
      const { result } = await api.torBridgeSources.fetch(s.id);
      await reload();
      if (result.status === 'ok') {
        toast.success(
          `${s.url}: ${result.added} new of ${result.total} (dup: ${result.skippedDup}, bad: ${result.skippedBad}).`,
        );
        if (result.added > 0 && onBridgesAdded) await onBridgesAdded();
      } else {
        toast.error(`${s.url}: ${result.errorMessage}`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Fetch failed.');
    } finally {
      setFetching((set) => {
        const next = new Set(set);
        next.delete(s.id);
        return next;
      });
    }
  }

  async function handleFetchAll() {
    if (!sources || sources.length === 0) {
      toast.error('No sources to fetch.');
      return;
    }
    const ids = sources.filter((s) => s.enabled).map((s) => s.id);
    if (ids.length === 0) {
      toast.error('No enabled sources.');
      return;
    }
    setBusy(true);
    setFetching(new Set(ids));
    try {
      const summary = await api.torBridgeSources.fetchAll();
      await reload();
      const errors = (summary.results || []).filter((r) => r.status !== 'ok').length;
      toast.success(
        `Fetched ${summary.count} source(s): ${summary.totalAdded} new bridge(s) added.` +
        (errors > 0 ? ` ${errors} source(s) errored.` : ''),
      );
      if (summary.totalAdded > 0 && onBridgesAdded) await onBridgesAdded();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Fetch-all failed.');
    } finally {
      setBusy(false);
      setFetching(new Set());
    }
  }

  async function handleToggleEnabled(s) {
    setBusy(true);
    try {
      await api.torBridgeSources.update(s.id, { enabled: !s.enabled });
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Toggle failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Delete this source?\n\n${s.url}`)) return;
    setBusy(true);
    try {
      await api.torBridgeSources.remove(s.id);
      await reload();
      toast.success('Source deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  function openEdit(s) {
    setEditing({
      id:   s.id,
      name: s.name || '',
      url:  s.url,
      kind: s.kind || '',
    });
  }
  async function handleEditSave({ id, name, url, kind }) {
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    setBusy(true);
    try {
      await api.torBridgeSources.update(id, {
        name: name.trim(),
        url:  url.trim(),
        kind: kind || null,
      });
      await reload();
      setEditing(null);
      toast.success('Source updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ink">Sources</h3>
          <p className="text-xs text-mist">
            Public URLs that publish bridge lines (one per non-blank, non-comment
            line; <code className="rounded bg-surface-strong px-1">#</code> and{' '}
            <code className="rounded bg-surface-strong px-1">//</code> lines
            ignored). Press <b>Fetch now</b> on a saved source to pull its URL
            and import any new bridges.
          </p>
        </div>
        {sources && sources.length > 0 && (
          <button
            type="button"
            className="btn-ghost text-accent"
            onClick={handleFetchAll}
            disabled={busy}
            title="Fetch every enabled source in sequence"
          >
            <i className="fa-solid fa-rotate mr-1.5" aria-hidden="true" />
            Fetch all
          </button>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(180px,1fr)_minmax(280px,2fr)_140px_auto]">
        <input
          type="text"
          className="field"
          placeholder="Name *"
          maxLength={120}
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          disabled={adding}
          title="Required — shown as the source label everywhere (rows here + Schedule modal)."
        />
        <input
          type="url"
          className="field font-mono text-xs"
          placeholder="https://raw.githubusercontent.com/.../bridges-obfs4"
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          disabled={adding}
        />
        <select
          className="field"
          value={draftKind}
          onChange={(e) => setDraftKind(e.target.value)}
          disabled={adding}
          title="If the URL serves one type — preset it; otherwise leave 'auto' and we re-detect per line."
        >
          <option value="">auto-detect</option>
          <option value="obfs4">obfs4</option>
          <option value="snowflake">snowflake</option>
          <option value="meek_lite">meek_lite</option>
          <option value="webtunnel">webtunnel</option>
          <option value="vanilla">vanilla</option>
        </select>
        <button
          type="button"
          className="btn-primary"
          onClick={handleAdd}
          disabled={adding || draftName.trim().length === 0 || draftUrl.trim().length === 0}
        >
          {adding ? 'Adding…' : 'Add source'}
        </button>
      </div>

      {sources && sources.length > 0 && (
        <div className="divide-y divide-line rounded-xl ring-1 ring-line">
          {sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              busy={busy}
              fetching={fetching.has(s.id)}
              onToggleEnabled={() => handleToggleEnabled(s)}
              onFetch={() => handleFetch(s)}
              onEdit={() => openEdit(s)}
              onDelete={() => handleDelete(s)}
            />
          ))}
        </div>
      )}

      {sources && sources.length === 0 && (
        <p className="text-xs text-mist">
          No sources yet. Add a URL above and press <b>Fetch now</b> on the
          new row to import a bridge batch.
        </p>
      )}

      {editing && (
        <EditSourceModal
          initial={editing}
          busy={busy}
          onSave={handleEditSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function SourceRow({ source, busy, fetching, onToggleEnabled, onFetch, onEdit, onDelete }) {
  const lastStatus = source.lastStatus
    ? (source.lastStatus.startsWith('error')
        ? { icon: 'fa-circle-xmark', cls: 'text-rose-600' }
        : { icon: 'fa-circle-check', cls: 'text-emerald-600' })
    : null;
  return (
    <div className={
      'flex items-start gap-3 px-4 py-3 ' +
      (source.enabled ? '' : 'opacity-60')
    }>
      <label className="mt-1 inline-flex shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          checked={source.enabled}
          onChange={onToggleEnabled}
          disabled={busy}
          className="h-4 w-4"
          title={source.enabled ? 'Disable (skipped by Fetch all)' : 'Enable'}
        />
      </label>
      <div className="min-w-0 flex-1">
        {/* Name is now the primary label (Round 63 follow-up). URL falls
            back to it for legacy rows that haven't been edited yet. */}
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-ink" title={source.name || source.url}>
            {source.name || source.url}
          </span>
          {source.kind && (
            <span className="shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">
              {source.kind}
            </span>
          )}
        </div>
        <div className="mt-0.5 break-all font-mono text-[11px] text-mist" title={source.url}>
          {source.url}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-mist">
          {lastStatus && (
            <span className={lastStatus.cls}>
              <i className={`fa-solid ${lastStatus.icon} mr-1`} aria-hidden="true" />
              {source.lastStatus}
            </span>
          )}
          {source.lastFetchedAt && (
            <span title="Last fetch attempt">
              <i className="fa-solid fa-clock mr-1" aria-hidden="true" />
              {new Date(source.lastFetchedAt).toLocaleString()}
            </span>
          )}
          {source.lastAdded != null && (
            <span title="New bridges imported on last fetch">
              <i className="fa-solid fa-plus mr-1" aria-hidden="true" />
              {source.lastAdded} added
            </span>
          )}
          {source.lastSkippedDup != null && (
            <span title="Duplicates skipped on last fetch">
              <i className="fa-solid fa-clone mr-1" aria-hidden="true" />
              {source.lastSkippedDup} dup
            </span>
          )}
          {source.lastSkippedBad != null && (
            <span title="Invalid lines skipped on last fetch">
              <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden="true" />
              {source.lastSkippedBad} bad
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          className="btn-ghost text-accent"
          onClick={onFetch}
          disabled={busy || fetching}
          title="Fetch now — pull URL + import new bridges"
        >
          <i className={fetching
            ? 'fa-solid fa-spinner fa-spin'
            : 'fa-solid fa-cloud-arrow-down'} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn-ghost text-ink"
          onClick={onEdit}
          disabled={busy}
          title="Edit"
        >
          <i className="fa-solid fa-pen" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn-ghost text-rose-600 hover:bg-rose-soft"
          onClick={onDelete}
          disabled={busy}
          title="Delete"
        >
          <i className="fa-solid fa-trash" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function EditSourceModal({ initial, busy, onSave, onClose }) {
  const [name, setName] = useState(initial.name || '');
  const [url,  setUrl]  = useState(initial.url);
  const [kind, setKind] = useState(initial.kind);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function onSubmit(e) {
    e.preventDefault();
    onSave({ id: initial.id, name, url, kind });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-2xl rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-line"
      >
        <header className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <i className="fa-solid fa-pen" aria-hidden="true" />
          </div>
          <h2 className="text-base font-semibold text-ink">Edit source</h2>
        </header>
        <div className="space-y-4">
          <div>
            <label htmlFor="edit_src_name" className="field-label">Name</label>
            <input
              id="edit_src_name"
              type="text"
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              maxLength={120}
              required
              autoFocus
            />
            <p className="mt-1 text-[11px] text-mist">
              Shown wherever the source is listed (rows here + Schedule modal).
            </p>
          </div>
          <div>
            <label htmlFor="edit_src_url" className="field-label">URL</label>
            <input
              id="edit_src_url"
              type="url"
              className="field font-mono text-xs"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <label htmlFor="edit_src_kind" className="field-label">Kind override</label>
            <select
              id="edit_src_kind"
              className="field"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              disabled={busy}
            >
              <option value="">auto-detect</option>
              <option value="obfs4">obfs4</option>
              <option value="snowflake">snowflake</option>
              <option value="meek_lite">meek_lite</option>
              <option value="webtunnel">webtunnel</option>
              <option value="vanilla">vanilla</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || name.trim().length === 0 || url.trim().length === 0}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Edit modal — adjust line and comment in place. Pre-fills with the
 * row's current values; submit hits PATCH /api/tor-bridges/:id.
 *
 * Closes on backdrop click, ESC, or explicit Cancel / Save success.
 */
function EditBridgeModal({ initial, busy, onSave, onClose }) {
  const [line, setLine] = useState(initial.line);
  const [comment, setComment] = useState(initial.comment);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function onSubmit(e) {
    e.preventDefault();
    onSave({ id: initial.id, line, comment });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-2xl rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-line"
      >
        <header className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <i className="fa-solid fa-pen" aria-hidden="true" />
          </div>
          <h2 className="text-base font-semibold text-ink">Edit bridge</h2>
        </header>
        <div className="space-y-4">
          <div>
            <label htmlFor="edit_line" className="field-label">Bridge line</label>
            <textarea
              id="edit_line"
              rows={3}
              className="field font-mono text-xs"
              value={line}
              onChange={(e) => setLine(e.target.value)}
              disabled={busy}
              autoFocus
            />
            <p className="mt-1 text-[11px] text-mist">
              The bridge kind is re-detected from the first token; saving
              with a different prefix (e.g. obfs4 → snowflake) re-categorises
              the row into another section.
            </p>
          </div>
          <div>
            <label htmlFor="edit_comment" className="field-label">Comment (optional)</label>
            <input
              id="edit_comment"
              type="text"
              className="field"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={busy}
              maxLength={512}
              placeholder="e.g. provided by alice@vendor, valid until 2026-09"
            />
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || line.trim().length === 0}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
